import { useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { cn } from "@pontimed/ui";
import { AnimatePresence, m } from "framer-motion";
import { ChevronDown, Wallet } from "lucide-react";

import type { PlanPriceResult } from "@/lib/pricing/calculate-plan-price";

import type { CheckoutState, CheckoutStep } from "./checkout-types";

// Legacy source: src/components/billing/checkout/checkout-summary.tsx (1:1 port).
// Legacy used next-intl namespace "checkout.summary" with a dynamic key
// t(`entityType_${state.entityType}`); Lingui needs static msgids, so the
// entity labels live in a per-type map below (legacy keys were
// entityType_user / entityType_doctor / entityType_business / entityType_location).

interface CheckoutSummaryProps {
  state: CheckoutState;
  price: PlanPriceResult | null;
  currentStep: CheckoutStep;
  variant?: "desktop" | "mobile";
  className?: string;
}

export function CheckoutSummary({
  state,
  price,
  currentStep,
  variant,
  className,
}: CheckoutSummaryProps) {
  const { t } = useLingui();
  const [mobileExpanded, setMobileExpanded] = useState(false);

  // Don't show summary on plan step (nothing selected yet) or success
  if (currentStep === "plan" || currentStep === "success" || currentStep === "error") return null;
  if (!state.selectedPlanCode || !price) return null;

  const entityTypeLabels: Record<CheckoutState["entityType"], string> = {
    user: t`User subscription`,
    provider: t`Doctor subscription`,
    business: t`Business subscription`,
    facility: t`Clinic subscription`,
  };

  const sym = "€";
  const fmt = (n: number) => `${sym}${Math.round(n * 100) / 100}`;
  const totalNow = state.interval === "annual" ? price.intervalTotal : price.monthlyTotal;
  const walletCredit = Math.min(state.walletBalance, totalNow);
  const dueNow = Math.round((totalNow - walletCredit) * 100) / 100;

  const selectedProfile = state.billingProfiles.find((p) => p.id === state.selectedProfileId);
  const selectedCard = state.savedCards.find((c) => c.id === state.selectedCardId);

  const renewalDate = new Date();
  if (state.interval === "annual") {
    renewalDate.setFullYear(renewalDate.getFullYear() + 1);
  } else {
    renewalDate.setMonth(renewalDate.getMonth() + 1);
  }

  const content = (
    <div className="space-y-3 text-sm">
      {/* Plan + entity type */}
      <div>
        <div className="text-muted-foreground text-xs">
          <Trans>Plan</Trans>
        </div>
        <div className="text-foreground font-semibold">{state.selectedPlanName}</div>
        <div className="text-muted-foreground mt-0.5 text-[0.625rem]">
          {entityTypeLabels[state.entityType]}
        </div>
      </div>

      {/* Interval */}
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground text-xs">
          <Trans>Interval</Trans>
        </span>
        <span className="text-foreground text-xs font-medium">
          {state.interval === "annual" ? t`annual` : t`monthly`}
        </span>
      </div>

      {/* Quantity */}
      {state.quantity > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground text-xs">
            <Trans>Licenses</Trans>
          </span>
          <span className="text-foreground text-xs font-medium">{state.quantity}</span>
        </div>
      )}

      {/* Billing profile */}
      {selectedProfile && currentStep !== "configure" && (
        <div>
          <div className="text-muted-foreground text-xs">
            <Trans>Billing</Trans>
          </div>
          <div className="text-foreground text-xs font-medium">
            {selectedProfile.businessName || selectedProfile.profileName}
          </div>
        </div>
      )}

      {/* Payment method */}
      {selectedCard && currentStep === "pay" && (
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground text-xs">
            <Trans>Payment method</Trans>
          </span>
          <span className="text-foreground text-xs font-medium">
            {selectedCard.brand} ····{selectedCard.last4}
          </span>
        </div>
      )}

      {/* Divider */}
      <div className="border-border border-t" />

      {/* Price breakdown */}
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground text-xs">
          {state.interval === "annual" ? t`Total / annual` : t`Total / monthly`}
        </span>
        <span className="text-foreground font-semibold tabular-nums">{fmt(totalNow)}</span>
      </div>

      {state.interval === "annual" && (
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground text-xs">
            ≈ <Trans>per month</Trans>
          </span>
          <span className="text-muted-foreground text-xs tabular-nums">
            {fmt(Math.round(price.intervalTotal / 12))}
          </span>
        </div>
      )}

      {/* Wallet credit */}
      {walletCredit > 0 && (
        <>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground flex items-center gap-1 text-xs">
              <Wallet className="size-3" />
              <Trans>Wallet credit</Trans>
            </span>
            <span className="text-xs font-medium text-emerald-600 tabular-nums">
              -{fmt(walletCredit)}
            </span>
          </div>
          <div className="border-border border-t" />
          <div className="flex items-center justify-between">
            <span className="text-foreground text-sm font-bold">
              <Trans>Due now</Trans>
            </span>
            <span className="text-foreground text-lg font-bold tabular-nums">
              {dueNow > 0 ? fmt(dueNow) : t`Free`}
            </span>
          </div>
        </>
      )}

      {/* Due now (no wallet) */}
      {walletCredit === 0 && (
        <div className="flex items-center justify-between">
          <span className="text-foreground text-sm font-bold">
            <Trans>Due now</Trans>
          </span>
          <span className="text-foreground text-lg font-bold tabular-nums">{fmt(dueNow)}</span>
        </div>
      )}

      {/* Renewal info */}
      <div className="text-muted-foreground space-y-0.5 text-[0.625rem]">
        <div className="flex items-center justify-between">
          <span>
            <Trans>At renewal</Trans>
          </span>
          <span>
            {fmt(totalNow)}/{state.interval === "annual" ? t`annual` : t`monthly`}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span>
            <Trans>Next payment</Trans>
          </span>
          <span>
            {renewalDate.toLocaleDateString(undefined, {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      {variant !== "mobile" && (
        <div className={cn("hidden w-[280px] shrink-0 border-l p-5 md:block", className)}>
          <div className="text-foreground mb-3 text-sm font-bold">
            <Trans>Order summary</Trans>
          </div>
          {content}
        </div>
      )}

      {/* Mobile collapsible strip */}
      {variant !== "desktop" && (
        <div className={cn("md:hidden", className)}>
          <button
            onClick={() => setMobileExpanded((v) => !v)}
            className="flex w-full items-center justify-between px-5 py-3"
          >
            <div className="flex items-center gap-2">
              <span className="text-foreground text-sm font-semibold">
                {dueNow > 0 ? fmt(dueNow) : t`Free`}
              </span>
              <span className="text-muted-foreground text-xs">
                {state.selectedPlanName} · {state.interval === "annual" ? t`annual` : t`monthly`}
              </span>
            </div>
            <ChevronDown
              className={cn(
                "text-muted-foreground size-4 transition-transform",
                mobileExpanded && "rotate-180",
              )}
            />
          </button>
          <AnimatePresence>
            {mobileExpanded && (
              <m.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden px-5 pb-4"
              >
                {content}
              </m.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </>
  );
}
